/* Theme custom file */
const OPERATION_REMINDER_STYLES_ID = 'operation-reminder-styles';
const KLAVIYO_CLIENT_API = 'https://a.klaviyo.com/client';
const KLAVIYO_COMPANY_ID = 'KQsfB7';
const KLAVIYO_REVISION = '2026-07-15';

class OperationReminder extends HTMLElement {
  static STEPS = { IDLE: 'idle', FORM: 'form', SUBMITTING: 'submitting', SUCCESS: 'success', ERROR: 'error' };

  static STORAGE_KEY = 'operation-reminders';

  connectedCallback() {
    OperationReminder.injectStyles();

    // list-id is the global Klaviyo list the subscribe call adds profiles to,
    // shared across all operations. operation-handle keys the per-operation
    // subscription state so one subscribe does not mark every operation as
    // subscribed.
    this.listId = this.getAttribute('list-id') || '';
    this.operationKey = this.getAttribute('operation-handle') || this.listId;
    this.endDate = (this.dataset.operationEndDate || '').slice(0, 10);
    this.source = this.dataset.source || 'coming-soon-card';
    this.customerEmail = this.dataset.customerEmail || '';

    // Read identity context from header-component (present on all pages)
    const header = document.getElementById('header-component');
    this.currentLoggedIn = header?.hasAttribute('data-customer-logged-in') ?? false;
    this.currentEmail = header?.dataset.customerEmail || '';

    if (this.isAlreadySubscribed()) {
      this.dataset.step = OperationReminder.STEPS.SUCCESS;
    } else {
      this.dataset.step = OperationReminder.STEPS.IDLE;
    }

    this.alertBtn = this.querySelector('[data-operation-reminder-trigger]');
    this.formEl = this.querySelector('.operation-reminder__form');
    this.emailInput = this.querySelector('.operation-reminder__input');
    this.submitBtn = this.querySelector('.operation-reminder__submit');
    this.successEl = this.querySelector('.operation-reminder__success');
    this.errorEl = this.querySelector('.operation-reminder__error');
    this.bindEvents();

    if (this.customerEmail) {
      this.emailInput.value = this.customerEmail;
    }
  }

  disconnectedCallback() {
    this.alertBtn?.removeEventListener('click', this.handleTriggerClick);
    this.submitBtn?.removeEventListener('click', this.handleSubmit);
    clearTimeout(this.errorTimeout);
  }

  bindEvents() {
    this.handleTriggerClick = () => {
      if (this.customerEmail) {
        this.submit();
      } else {
        this.goTo(OperationReminder.STEPS.FORM);
      }
    };
    this.handleSubmit = () => this.submit();

    this.alertBtn?.addEventListener('click', this.handleTriggerClick);
    this.submitBtn.addEventListener('click', this.handleSubmit);

    this.emailInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.submit();
    });
  }

  goTo(step) {
    this.dataset.step = step;

    if (step === OperationReminder.STEPS.FORM) {
      requestAnimationFrame(() => this.emailInput.focus());
    }

    if (step === OperationReminder.STEPS.ERROR) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = setTimeout(() => this.goTo(OperationReminder.STEPS.FORM), 2000);
    }
  }

  async submit() {
    const email = this.customerEmail || this.emailInput.value.trim();

    if (!this.customerEmail) {
      if (!this.emailInput.reportValidity()) return;
      if (!email) return;
    }

    this.goTo(OperationReminder.STEPS.SUBMITTING);

    try {
      await this.submitToKlaviyo(email, this.listId);
      this.#trackUpcomingSale(email);
      this.saveSubscription();
      this.dispatchAnalytics(email);
      this.goTo(OperationReminder.STEPS.SUCCESS);
    } catch {
      this.goTo(OperationReminder.STEPS.ERROR);
    }
  }

  async submitToKlaviyo(email, listId) {
    await this.#postToKlaviyo('subscriptions', {
      type: 'subscription',
      attributes: {
        custom_source: this.source,
        profile: {
          data: {
            type: 'profile',
            attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
          },
        },
      },
      relationships: { list: { data: { type: 'list', id: listId } } },
    });
  }

  /** Records the upcoming sale signup so CRM flows can target it. Never blocks the UI. */
  #trackUpcomingSale(email) {
    if (!this.hasAttribute('data-upcoming-sale')) return;

    const properties = {
      operation_name: this.dataset.operationName || '',
      sale_type: this.dataset.saleType === 'physical_sale' ? 'physical_sale' : 'sale',
    };
    const startDate = new Date(this.dataset.operationStartDate || '');
    if (!Number.isNaN(startDate.getTime())) properties.start_date = startDate.toISOString();
    if (this.dataset.operationId) properties.internal_id = this.dataset.operationId;
    const isPhysicalSale = properties.sale_type === 'physical_sale';
    // A physical sale has no collection to open: CRM runs a separate flow on
    // its own metric and points the reminder at its page.
    if (isPhysicalSale && this.dataset.saleUrl) {
      properties.sale_url = this.dataset.saleUrl;
    }
    const metricName = isPhysicalSale ? 'subscribe_to_physical_sale' : 'subscribe_to_upcoming_sale';

    this.#postToKlaviyo('events', {
      type: 'event',
      attributes: {
        properties,
        metric: { data: { type: 'metric', attributes: { name: metricName } } },
        profile: { data: { type: 'profile', attributes: { email } } },
      },
    }).catch(() => { /* tracking never affects the subscribe flow */ });
  }

  async #postToKlaviyo(endpoint, data) {
    const response = await fetch(`${KLAVIYO_CLIENT_API}/${endpoint}?company_id=${KLAVIYO_COMPANY_ID}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({ data }),
    });

    if (!response.ok) throw new Error(`Klaviyo ${endpoint} failed`);
  }

  /**
   * Check if this operation was already subscribed to, factoring in identity.
   *
   * Storage format: { [operationHandle]: { email: string, loggedIn: boolean, endDate: string } }
   *
   * Rules:
   * - Logged in now: show success if stored entry email matches current account email
   * - Visitor now: show success if stored entry was made as visitor (loggedIn: false),
   *   OR if stored email matches current visitor context (no customer email to compare)
   */
  isAlreadySubscribed() {
    const entry = this.#loadStore()[this.operationKey];
    return entry ? this.#checkEntry(entry) : false;
  }

  /**
   * Load the subscription store, resetting legacy formats and pruning entries
   * for operations whose end date has passed. Persists the prune if it removed
   * anything so stale handles do not accumulate.
   *
   * @returns {Record<string, { email: string, loggedIn: boolean, endDate?: string }>}
   */
  #loadStore() {
    let stored;
    try {
      stored = JSON.parse(localStorage.getItem(OperationReminder.STORAGE_KEY) || '{}');
    } catch {
      return {};
    }

    // Legacy array format held bare list IDs with no identity or date context
    // and cannot be reconciled to operation handles, so start fresh.
    if (stored === null || typeof stored !== 'object' || Array.isArray(stored)) {
      return {};
    }

    const today = new Date().toISOString().slice(0, 10);
    let changed = false;
    for (const key of Object.keys(stored)) {
      const endDate = stored[key]?.endDate;
      if (endDate && endDate < today) {
        delete stored[key];
        changed = true;
      }
    }

    if (changed) {
      try {
        localStorage.setItem(OperationReminder.STORAGE_KEY, JSON.stringify(stored));
      } catch { /* localStorage unavailable */ }
    }

    return stored;
  }

  /** @param {{ email: string, loggedIn: boolean }} entry */
  #checkEntry(entry) {
    // If emails match, always show success regardless of login state transition
    if (entry.email && this.currentEmail && entry.email === this.currentEmail) {
      return true;
    }

    if (this.currentLoggedIn) {
      // Logged in, emails don't match: not this account's subscription
      return false;
    }

    // Visitor: show success only if entry was also made as visitor
    return !entry.loggedIn;
  }

  saveSubscription() {
    try {
      const stored = this.#loadStore();
      const email = this.currentLoggedIn ? this.currentEmail : (this.emailInput?.value.trim() || '');
      stored[this.operationKey] = { email, loggedIn: this.currentLoggedIn, endDate: this.endDate };
      localStorage.setItem(OperationReminder.STORAGE_KEY, JSON.stringify(stored));
    } catch { /* localStorage unavailable */ }
  }

  dispatchAnalytics(email) {
    document.dispatchEvent(new CustomEvent('subscribeToSale', {
      detail: {
        brand_name: this.dataset.operationName || '',
        start_date: this.dataset.operationStartDate || '',
        end_date: this.dataset.operationEndDate || '',
      },
    }));

    if (typeof klaviyo !== 'undefined') {
      klaviyo.identify({ email });
    }
  }

  static injectStyles() {
    if (document.getElementById(OPERATION_REMINDER_STYLES_ID)) return;

    const style = document.createElement('style');
    style.id = OPERATION_REMINDER_STYLES_ID;
    style.textContent = `
      operation-reminder {
        display: block;
      }

      /* Step visibility */
      operation-reminder[data-step="idle"] .operation-reminder__form,
      operation-reminder[data-step="idle"] .operation-reminder__success,
      operation-reminder[data-step="idle"] .operation-reminder__error,
      operation-reminder[data-step="form"] [data-operation-reminder-trigger],
      operation-reminder[data-step="form"] .operation-reminder__success,
      operation-reminder[data-step="form"] .operation-reminder__error,
      operation-reminder[data-step="submitting"] .operation-reminder__form,
      operation-reminder[data-step="submitting"] .operation-reminder__success,
      operation-reminder[data-step="submitting"] .operation-reminder__error,
      operation-reminder[data-step="success"] [data-operation-reminder-trigger],
      operation-reminder[data-step="success"] .operation-reminder__form,
      operation-reminder[data-step="success"] .operation-reminder__error,
      operation-reminder[data-step="error"] [data-operation-reminder-trigger],
      operation-reminder[data-step="error"] .operation-reminder__form,
      operation-reminder[data-step="error"] .operation-reminder__success {
        display: none !important;
      }

      /* Form container — glass pill */
      .operation-reminder__form {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        padding: 12px;
        height: 40px;
        background: rgba(201, 201, 201, 0.26);
        backdrop-filter: blur(22px);
        border-radius: 6px;
        box-sizing: border-box;
      }

      /* Email input — transparent, inherits pill styling */
      .operation-reminder__input {
        flex: 1;
        min-width: 0;
        border: none !important;
        background: transparent !important;
        outline: none;
        padding: 0;
        margin: 0;
        font-family: 'DM Sans', var(--font-body--family);
        font-weight: 600;
        font-size: 10px;
        line-height: 120%;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.7);
      }

      .operation-reminder__input::placeholder {
        color: rgba(255, 255, 255, 0.7);
      }

      /* Submit button */
      .operation-reminder__submit {
        flex-shrink: 0;
        border: none;
        background: transparent;
        padding: 0;
        margin: 0;
        cursor: pointer;
        font-family: 'DM Sans', var(--font-body--family);
        font-weight: 600;
        font-size: 10px;
        line-height: 120%;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        color: #fff;
        min-height: 0;
        min-width: 0;
      }

      /* Submitting state — show trigger as spinner */
      operation-reminder[data-step="submitting"] [data-operation-reminder-trigger] {
        pointer-events: none;
      }

      operation-reminder[data-step="submitting"] [data-operation-reminder-trigger] svg {
        display: none;
      }

      operation-reminder[data-step="submitting"] [data-operation-reminder-trigger]::after {
        content: '';
        width: 16px;
        height: 16px;
        border: 2px solid rgb(255 255 255 / 0.3);
        border-top-color: #fff;
        border-radius: 50%;
        animation: operation-reminder-spin 0.6s linear infinite;
      }

      @keyframes operation-reminder-spin {
        to { rotate: 360deg; }
      }

      /* Error container — same glass pill with shake */
      .operation-reminder__error {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 12px;
        height: 40px;
        background: rgba(201, 201, 201, 0.26);
        backdrop-filter: blur(22px);
        border-radius: 6px;
        box-sizing: border-box;
        font-family: 'DM Sans', var(--font-body--family);
        font-weight: 600;
        font-size: 10px;
        line-height: 120%;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.7);
        animation: operation-reminder-shake 0.3s ease-out;
      }

      @keyframes operation-reminder-shake {
        0%, 100% { transform: translateX(0); }
        20% { transform: translateX(-4px); }
        40% { transform: translateX(4px); }
        60% { transform: translateX(-2px); }
        80% { transform: translateX(2px); }
      }

      /* Success container — same glass pill */
      .operation-reminder__success {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 12px;
        height: 40px;
        background: rgba(201, 201, 201, 0.26);
        backdrop-filter: blur(22px);
        border-radius: 6px;
        box-sizing: border-box;
        font-family: 'DM Sans', var(--font-body--family);
        font-weight: 600;
        font-size: 10px;
        line-height: 120%;
        letter-spacing: 0.09em;
        text-transform: uppercase;
        color: #fff;
      }
    `;

    document.head.appendChild(style);
  }
}

customElements.define('operation-reminder', OperationReminder);
